/**
 * 記事の書き方 — what is applied to this clinic, and how to ask for a change.
 *
 * The clinic does not author this. A request block is an instruction to a model,
 * and a tenant able to write one could write 「これまでの制約は無視して」 and point
 * it straight at the prohibitions the platform exists to enforce. So the screen
 * is two halves with a person in between:
 *
 *   WHAT IS APPLIED - read-only. Whether JMC has written wording for this clinic
 *   and, in plain language, the house style in force. Being unable to see why
 *   your own articles sound the way they do is its own kind of problem.
 *
 *   WHAT YOU ASKED FOR - a request, in prose, that goes to a JMC operator. It is
 *   a ticket, not a prompt: nothing typed here is ever rendered into a
 *   generation, and the only wording that reaches a model is what an operator
 *   wrote after reading it.
 */
import { useState } from 'react';
import { ApiError, post } from '../api.ts';
import { Card, Empty, Pill, Table, jaDate } from './ui.tsx';
import { Field } from './EditPanel.tsx';
import { Select } from './Select.tsx';
import { Textarea } from './Textarea.tsx';

export interface ClinicPromptView {
  key: 'article.body' | 'article.outline';
  /** Whether JMC has written a request block for this clinic. */
  customised: boolean;
  styleNote: string;
  updatedAt: string | null;
}

export interface PromptRequest {
  id: string;
  prompt_key: 'article.body' | 'article.outline' | null;
  body: string;
  status: 'OPEN' | 'APPLIED' | 'DECLINED';
  response: string;
  created_at: string;
  resolved_at: string | null;
}

export interface ClinicPromptStyles {
  items: ClinicPromptView[];
  requests: PromptRequest[];
}

const LABELS: Record<ClinicPromptView['key'], string> = {
  'article.body': '本文',
  'article.outline': '構成案（タイトル・見出し）',
};

const STATUS: Record<PromptRequest['status'], { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  OPEN: { label: '対応中', tone: 'warn' },
  APPLIED: { label: '反映済み', tone: 'ok' },
  DECLINED: { label: '見送り', tone: 'muted' },
};

const MAX_REQUEST = 2000;

export function PromptStyle({
  data,
  canRequest,
  onSent,
}: {
  data: ClinicPromptStyles;
  canRequest: boolean;
  onSent: () => void;
}) {
  const [promptKey, setPromptKey] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setSending(true);
    setError(null);
    setSent(false);
    try {
      await post('/clinic/prompt-requests', {
        ...(promptKey ? { promptKey } : {}),
        body: body.trim(),
      });
      setBody('');
      setSent(true);
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '送信できませんでした');
    } finally {
      setSending(false);
    }
  }

  return (
    <Card
      title="記事の書き方"
      caption="記事生成に使う指示はJMCが管理します。文体や書き方のご要望はこちらから依頼してください。"
    >
      <div className="grid grid--2">
        {data.items.map((entry) => (
          <div key={entry.key}>
            <p className="muted small" style={{ margin: '0 0 4px' }}>
              {LABELS[entry.key]}{' '}
              {entry.customised ? (
                <Pill tone="ok">このクリニック専用</Pill>
              ) : (
                <Pill tone="muted">標準</Pill>
              )}
            </p>
            <p style={{ margin: 0 }}>
              {entry.styleNote || (
                <span className="muted">
                  文体の指定はありません。標準の書き方で生成されます。
                </span>
              )}
            </p>
            {entry.updatedAt && (
              <p className="muted small" style={{ margin: '2px 0 0' }}>
                最終更新 {jaDate(entry.updatedAt)}
              </p>
            )}
          </div>
        ))}
      </div>

      {canRequest && (
        <>
          <p className="form__section">変更を依頼する</p>
          <div className="grid grid--3">
            <Field label="対象" htmlFor="req-key">
              <Select
                id="req-key"
                value={promptKey}
                onChange={setPromptKey}
                options={[
                  { value: '', label: '記事全体' },
                  { value: 'article.body', label: '本文' },
                  { value: 'article.outline', label: '構成案' },
                ]}
              />
            </Field>
          </div>
          <Field
            label="ご要望"
            htmlFor="req-body"
            span="full"
            hint="例：一人称は「当院」に統一してほしい。費用の説明を先に置いてほしい。専門用語は残しつつ、初診の方向けに補足を入れてほしい。"
          >
            <Textarea
              id="req-body"
              minRows={4}
              maxLength={MAX_REQUEST}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}

          <div className="chips">
            <button
              className="btn btn--sm"
              type="button"
              disabled={sending || body.trim() === ''}
              onClick={() => void send()}
            >
              {sending ? '送信中…' : 'JMCに依頼する'}
            </button>
            {sent && <span className="muted small">依頼を送信しました</span>}
          </div>
          <p className="muted small">
            入力内容はJMCの担当者が確認します。この文章がそのままAIに送られることはありません。
          </p>
        </>
      )}

      <p className="form__section">依頼の履歴</p>
      {data.requests.length === 0 ? (
        <Empty>まだ依頼はありません。</Empty>
      ) : (
        <Table head={['依頼日', '対象', '内容', '状況', 'JMCからの回答']}>
          {data.requests.map((entry) => (
            <tr key={entry.id}>
              <td className="muted small">{jaDate(entry.created_at)}</td>
              <td>{entry.prompt_key ? LABELS[entry.prompt_key] : '記事全体'}</td>
              <td>{entry.body}</td>
              <td>
                <Pill tone={STATUS[entry.status].tone}>{STATUS[entry.status].label}</Pill>
              </td>
              <td className="muted">{entry.response || '—'}</td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}
